import { stealthnetLogoDataUrl } from "../assets/stealthnetLogoData.js";

function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function applyInlineFormatting(value) {
  return escapeHtml(value)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[\s(])\*(.+?)\*(?=[\s).,!?:;]|$)/g, "$1<em>$2</em>");
}

function splitParagraphs(value) {
  return String(value || "")
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);
}

function formatDisplayDate(value) {
  if (!value) {
    return "-";
  }

  const isoLikeMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  if (!isoLikeMatch) {
    return value;
  }

  const [, year, month, day] = isoLikeMatch;
  return `${month}/${day}/${year}`;
}

function defaultProjectDetailsOverview(report) {
  const clientName = report.client_name || "The client";
  const assessmentType = report.assessment_type || "security assessment";
  const startDate = formatDisplayDate(report.start_date);

  return `${clientName} enlisted the expertise of StealthNet AI to conduct a thorough assessment of their environment. This report meticulously outlines the findings and vulnerabilities uncovered during the ${assessmentType.toLowerCase()}, which commenced on ${startDate}. The results provided herein are a comprehensive reflection of the security analysis performed during this period.`;
}

function defaultConfidentialityNotice() {
  return [
    "This engagement was carried out by StealthNet AI in strict accordance with the Statement of Work. The scope of our procedures was confined to those specifically outlined in the agreement, ensuring a focused and methodical approach to the assessment. The findings and recommendations resulting from our work are detailed in the attached report.",
    "It's important to note that the time-boxed nature of this assessment, combined with our reliance on information provided by the client, means that the findings in this report represent a snapshot of the current security posture. As such, this report should not be regarded as an exhaustive identification of all possible security vulnerabilities. StealthNet AI advises ongoing vigilance and further assessments to ensure a comprehensive security strategy.",
  ].join("\n\n");
}

const PROJECT_DETAILS_OUTLINE_SECTIONS = [
  { id: "section-project-details-overview", label: "Overview" },
  { id: "section-project-details-dates", label: "Dates" },
  { id: "section-project-details-findings", label: "Findings" },
  { id: "section-project-details-scope", label: "Scope" },
];

const FINDING_OUTLINE_SECTIONS = [
  { suffix: "definition", label: "Definition" },
  { suffix: "details", label: "Details" },
  { suffix: "location", label: "Location" },
  { suffix: "remediation", label: "Remediation" },
];

function renderRichText(value) {
  const blocks = getRichTextBlocks(value);
  if (!blocks.length) {
    return "<p>[Not generated yet]</p>";
  }
  return blocks
    .map((block) => {
      if (block.type === "ul") {
        return `<ul>${block.items.map((item) => `<li>${applyInlineFormatting(item)}</li>`).join("")}</ul>`;
      }

      if (block.type === "ol") {
        return `<ol>${block.items.map((item) => `<li>${applyInlineFormatting(item)}</li>`).join("")}</ol>`;
      }

      return `<p>${block.lines.map((line) => applyInlineFormatting(line)).join("<br>")}</p>`;
    })
    .join("");
}

export function getRichTextBlocks(value) {
  return splitParagraphs(value).map((block) => {
    const lines = block.split("\n").map((line) => line.trimEnd()).filter(Boolean);
    const bulletLike = lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line));
    const numberedLike = lines.length > 0 && lines.every((line) => /^\d+\.\s+/.test(line));

    if (bulletLike) {
      return {
        type: "ul",
        items: lines.map((line) => line.replace(/^[-*]\s+/, "")),
      };
    }

    if (numberedLike) {
      return {
        type: "ol",
        items: lines.map((line) => line.replace(/^\d+\.\s+/, "")),
      };
    }

    return {
      type: "p",
      lines,
    };
  });
}

export function getSeverityPresentation(severity) {
  const normalized = String(severity || "informational").toLowerCase();
  const severityMap = {
    critical: { label: "Critical", color: "#6f42c1" },
    high: { label: "High", color: "#c62828" },
    medium: { label: "Medium", color: "#ef6c00" },
    low: { label: "Low", color: "#1565c0" },
    informational: { label: "Informational", color: "#2e7d32" },
  };

  return severityMap[normalized] || {
    label: severity || "Informational",
    color: "#2e7d32",
  };
}

export function buildFindingDocument(finding) {
  const definition = finding.final_definition || finding.generated_definition || "";
  const details = finding.final_details || finding.generated_details || "";
  const remediation = finding.final_remediation || finding.generated_remediation || "";

  return {
    title: finding.name || "Untitled Finding",
    severity: getSeverityPresentation(finding.severity),
    sections: [
      { key: "definition", title: "Definition", value: definition },
      { key: "details", title: "Details", value: details },
      { key: "location", title: "Location", value: finding.location || "" },
      { key: "remediation", title: "Remediation", value: remediation },
    ],
  };
}

export function buildProjectDetailsDocument(report) {
  const findings = report.findings || [];
  const derivedCounts = {
    critical: findings.filter((finding) => (finding.severity || "").toLowerCase() === "critical").length,
    high: findings.filter((finding) => (finding.severity || "").toLowerCase() === "high").length,
    medium: findings.filter((finding) => (finding.severity || "").toLowerCase() === "medium").length,
    low: findings.filter((finding) => (finding.severity || "").toLowerCase() === "low").length,
    informational: findings.filter((finding) => (finding.severity || "").toLowerCase() === "informational").length,
  };

  return {
    overview: report.project_details_overview || defaultProjectDetailsOverview(report),
    dates: [
      { key: "start_date", label: "Start", value: formatDisplayDate(report.start_date) },
      { key: "active_testing_date", label: "Active Testing", value: formatDisplayDate(report.active_testing_date) },
      { key: "report_delivery_date", label: "Report Delivery", value: formatDisplayDate(report.report_delivery_date) },
    ],
    findings: [
      { key: "critical_count_override", label: "Critical", value: report.critical_count_override ?? derivedCounts.critical },
      { key: "high_count_override", label: "High", value: report.high_count_override ?? derivedCounts.high },
      { key: "medium_count_override", label: "Medium", value: report.medium_count_override ?? derivedCounts.medium },
      { key: "low_count_override", label: "Low", value: report.low_count_override ?? derivedCounts.low },
      { key: "informational_count_override", label: "Informational", value: report.informational_count_override ?? derivedCounts.informational },
    ],
    scope: report.scope_summary || "",
  };
}

export function buildConfidentialityNoticeDocument(report) {
  return {
    body: report.confidentiality_notice || defaultConfidentialityNotice(),
  };
}

export function buildCoverPageDocument(report) {
  const coverDateValue = report.report_delivery_date || report.start_date || "";

  return {
    dateValue: coverDateValue,
    dateDisplay: coverDateValue ? formatDisplayDate(coverDateValue) : "Add report delivery date",
    title: "Penetration Testing Report",
    subtitle: report.assessment_type || "Security Assessment",
    clientName: report.client_name || "Client Name",
    brandName: "StealthNet AI",
    logoLabel: "stealthnet.ai",
  };
}

export function buildReportOutline(report) {
  const findings = report.findings || [];
  const pageNumbers = {
    cover: 1,
    tableOfContents: 2,
    confidentialityNotice: 3,
    projectDetails: 4,
    executiveSummary: 5,
    methodology: 6,
    findings: 7,
  };

  const tocEntries = [
    {
      id: "section-confidentiality-notice",
      label: "Confidentiality Notice",
      level: 1,
      pageNumber: pageNumbers.confidentialityNotice,
    },
    {
      id: "section-project-details",
      label: "Project Details",
      level: 1,
      pageNumber: pageNumbers.projectDetails,
    },
    ...PROJECT_DETAILS_OUTLINE_SECTIONS.map((section) => ({
      id: section.id,
      label: section.label,
      level: 2,
      pageNumber: pageNumbers.projectDetails,
    })),
    {
      id: "section-executive-summary",
      label: "Executive Summary",
      level: 1,
      pageNumber: pageNumbers.executiveSummary,
    },
    {
      id: "section-methodology",
      label: "Methodology",
      level: 1,
      pageNumber: pageNumbers.methodology,
    },
    {
      id: "section-findings",
      label: "Findings",
      level: 1,
      pageNumber: pageNumbers.findings,
    },
    ...findings.flatMap((finding, index) => {
      const pageNumber = pageNumbers.findings + index;
      const findingId = `finding-${finding.id}`;
      return [
        {
          id: findingId,
          label: finding.name || `Untitled Finding ${index + 1}`,
          level: 2,
          pageNumber,
        },
        ...FINDING_OUTLINE_SECTIONS.map((section) => ({
          id: `${findingId}-${section.suffix}`,
          label: section.label,
          level: 3,
          pageNumber,
        })),
      ];
    }),
  ];

  return {
    coverPage: {
      id: "section-cover-page",
      label: "Cover Page",
      pageNumber: pageNumbers.cover,
    },
    tocPage: {
      id: "section-table-of-contents",
      label: "Table of Contents",
      pageNumber: pageNumbers.tableOfContents,
    },
    tocEntries,
    navigationEntries: [
      {
        id: "section-cover-page",
        label: "Cover Page",
        level: 1,
        pageNumber: pageNumbers.cover,
      },
      {
        id: "section-table-of-contents",
        label: "Table of Contents",
        level: 1,
        pageNumber: pageNumbers.tableOfContents,
      },
      ...tocEntries,
    ],
  };
}

export function buildReportTableOfContentsDocument(report) {
  const outline = buildReportOutline(report);
  return {
    title: outline.tocPage.label,
    entries: outline.tocEntries,
  };
}

export function buildCoverPageHtml(report) {
  const cover = buildCoverPageDocument(report);
  const baseStyle =
    "position:relative; min-height:9.55in; padding:54pt; overflow:hidden; color:#ffffff; font-family:'Poppins', Arial, sans-serif; background:linear-gradient(155deg, #0b2033 0%, #0f3c61 42%, #134d7f 100%);";
  const shellAccentStyle =
    "position:absolute; inset:auto -10% -12% auto; width:320pt; height:320pt; border-radius:999px; background:radial-gradient(circle, rgba(115,190,255,0.34) 0%, rgba(115,190,255,0) 72%);";
  const shellAccentTwoStyle =
    "position:absolute; inset:10% auto auto -8%; width:260pt; height:260pt; border-radius:999px; background:radial-gradient(circle, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 72%);";
  const logoWrapStyle =
    "position:absolute; top:42pt; right:42pt; z-index:1; width:82pt; height:82pt; border-radius:20pt; display:flex; align-items:center; justify-content:center; background:linear-gradient(180deg, rgba(255,255,255,0.1), rgba(255,255,255,0.04)); border:1px solid rgba(255,255,255,0.14); box-shadow:0 18pt 40pt rgba(5,12,22,0.22), inset 0 1pt 0 rgba(255,255,255,0.08);";
  const topDateStyle =
    "position:relative; z-index:1; margin:0; text-transform:uppercase; letter-spacing:0.22em; font-size:11pt; font-weight:600; text-align:center; color:rgba(255,255,255,0.82);";
  const centerWrapStyle =
    "position:relative; z-index:1; min-height:6.7in; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center;";
  const titleStyle = "margin:0; font-size:30pt; line-height:1.18; font-weight:700; color:#ffffff;";
  const subtitleStyle = "margin:12pt 0 0; font-size:15pt; line-height:1.4; color:rgba(255,255,255,0.88);";
  const footerStyle =
    "position:relative; z-index:1; margin-top:24pt; padding-top:20pt; border-top:1px solid rgba(255,255,255,0.22); display:flex; align-items:flex-end; justify-content:space-between; gap:18pt;";
  const footerLabelStyle =
    "margin:0 0 6pt; text-transform:uppercase; letter-spacing:0.16em; font-size:8pt; color:rgba(255,255,255,0.68);";
  const footerValueStyle = "margin:0; font-size:13pt; line-height:1.35; font-weight:600; color:#ffffff;";

  return `
    <section style="${baseStyle}">
      <div style="${shellAccentStyle}"></div>
      <div style="${shellAccentTwoStyle}"></div>
      <div style="${logoWrapStyle}">
        <img src="${stealthnetLogoDataUrl}" alt="StealthNet AI logo" style="width:52pt; height:auto; display:block;">
      </div>
      <p style="${topDateStyle}">${escapeHtml(cover.dateDisplay)}</p>
      <div style="${centerWrapStyle}">
        <h1 style="${titleStyle}">${escapeHtml(cover.title)}</h1>
        <p style="${subtitleStyle}">${escapeHtml(cover.subtitle)}</p>
      </div>
      <div style="${footerStyle}">
        <div>
          <p style="${footerLabelStyle}">Prepared By</p>
          <p style="${footerValueStyle}">${escapeHtml(cover.brandName)}</p>
        </div>
        <div style="text-align:right;">
          <p style="${footerLabelStyle}">Prepared For</p>
          <p style="${footerValueStyle}">${escapeHtml(cover.clientName)}</p>
        </div>
      </div>
    </section>
  `.trim();
}

export function buildFindingHtml(finding) {
  const document = buildFindingDocument(finding);
  const baseStyle = "font-family:'Poppins', Arial, sans-serif; font-size:12pt; line-height:1.5; color:#202124;";
  const h2Style = "font-family:'Poppins', Arial, sans-serif; font-size:18pt; line-height:1.4; margin:0 0 8pt;";
  const h3Style = "font-family:'Poppins', Arial, sans-serif; font-size:14pt; line-height:1.5; font-weight:700; margin:14pt 0 6pt;";
  const headerTableStyle = "width:100%; border-collapse:collapse; margin:0 0 12pt;";
  const titleCellStyle = "width:72%; vertical-align:top; padding:0 12pt 0 0;";
  const severityCellStyle = "width:28%; vertical-align:top; padding:0; text-align:right;";
  const severityStyle = `font-family:'Poppins', Arial, sans-serif; font-size:16pt; line-height:1.3; font-weight:700; color:${document.severity.color}; margin:0;`;

  return `
    <div style="${baseStyle}">
      <table style="${headerTableStyle}">
        <tr>
          <td style="${titleCellStyle}">
            <h2 style="${h2Style}">${escapeHtml(document.title)}</h2>
          </td>
          <td style="${severityCellStyle}">
            <p style="${severityStyle}">${escapeHtml(document.severity.label)}</p>
          </td>
        </tr>
      </table>
      ${document.sections
        .map(
          (section) => `
            <h3 style="${h3Style}">${escapeHtml(section.title)}</h3>
            ${renderRichText(section.value)}
          `,
        )
        .join("")}
    </div>
  `.trim();
}

export function buildProjectDetailsHtml(report) {
  const project = buildProjectDetailsDocument(report);
  const baseStyle = "font-family:'Poppins', Arial, sans-serif; font-size:12pt; line-height:1.5; color:#202124;";
  const h2Style = "font-family:'Poppins', Arial, sans-serif; font-size:18pt; line-height:1.4; margin:0 0 12pt; font-weight:700; color:#202124;";
  const h3Style = "font-family:'Poppins', Arial, sans-serif; font-size:14pt; line-height:1.5; font-weight:700; margin:20pt 0 8pt; color:#202124;";
  const tableStyle = "width:100%; border-collapse:collapse; margin:0 0 8pt;";
  const headerCellStyle = "padding:8pt 10pt; text-align:left; font-weight:700; border:1px solid #d7dde5; background:#f6f8fb;";
  const valueCellStyle = "padding:10pt; text-align:left; border:1px solid #d7dde5;";
  const findingsHeaderCellBaseStyle =
    "padding:8pt 10pt; text-align:center; font-weight:700; border:1px solid #d7dde5;";
  const findingsValueCellBaseStyle =
    "padding:10pt; text-align:center; border:1px solid #d7dde5; font-weight:600;";

  return `
    <div style="${baseStyle}">
      <h2 style="${h2Style}">Project Details</h2>
      ${renderRichText(project.overview)}

      <h3 style="${h3Style}">Dates</h3>
      <table style="${tableStyle}">
        <tr>
          ${project.dates.map((item) => `<th style="${headerCellStyle}">${escapeHtml(item.label)}</th>`).join("")}
        </tr>
        <tr>
          ${project.dates.map((item) => `<td style="${valueCellStyle}">${escapeHtml(item.value)}</td>`).join("")}
        </tr>
      </table>

      <h3 style="${h3Style}">Findings</h3>
      <table style="${tableStyle}">
        <tr>
          ${project.findings
            .map((item) => {
              const severity = getSeverityPresentation(item.label);
              const headerStyle = `${findingsHeaderCellBaseStyle} background:${severity.color}1a; color:${severity.color};`;
              return `<th style="${headerStyle}">${escapeHtml(item.label)}</th>`;
            })
            .join("")}
        </tr>
        <tr>
          ${project.findings
            .map((item) => {
              const severity = getSeverityPresentation(item.label);
              const valueStyle = `${findingsValueCellBaseStyle} background:${severity.color}0f; color:${severity.color};`;
              return `<td style="${valueStyle}">${escapeHtml(String(item.value))}</td>`;
            })
            .join("")}
        </tr>
      </table>

      <h3 style="${h3Style}">Scope</h3>
      ${project.scope ? renderRichText(project.scope) : "<p>[Add scope here]</p>"}
    </div>
  `.trim();
}

export function buildConfidentialityNoticeHtml(report) {
  const notice = buildConfidentialityNoticeDocument(report);
  const baseStyle = "font-family:'Poppins', Arial, sans-serif; font-size:12pt; line-height:1.5; color:#202124;";
  const h2Style = "font-family:'Poppins', Arial, sans-serif; font-size:18pt; line-height:1.4; margin:0 0 12pt; font-weight:700; color:#202124;";

  return `
    <div style="${baseStyle}">
      <h2 style="${h2Style}">Confidentiality Notice</h2>
      ${renderRichText(notice.body)}
    </div>
  `.trim();
}

export function buildTableOfContentsHtml(report) {
  const toc = buildReportTableOfContentsDocument(report);
  const baseStyle = "font-family:'Poppins', Arial, sans-serif; font-size:12pt; line-height:1.5; color:#202124;";
  const h2Style = "font-family:'Poppins', Arial, sans-serif; font-size:18pt; line-height:1.4; margin:0 0 14pt; font-weight:700; color:#202124;";
  const tableStyle = "width:100%; border-collapse:collapse;";

  return `
    <div style="${baseStyle}">
      <h2 style="${h2Style}">${escapeHtml(toc.title)}</h2>
      <table style="${tableStyle}">
        ${toc.entries
          .map((entry) => {
            const indent = entry.level === 1 ? "0" : entry.level === 2 ? "18pt" : "36pt";
            const fontWeight = entry.level === 1 ? "700" : entry.level === 2 ? "600" : "500";
            const fontSize = entry.level === 1 ? "12.5pt" : "12pt";
            const labelCellStyle = `padding:0 10pt 10pt ${indent}; font-weight:${fontWeight}; font-size:${fontSize}; vertical-align:bottom;`;
            const leaderCellStyle = "padding:0 8pt 10pt; width:100%; border-bottom:1px dotted #9aa0a6;";
            const pageCellStyle = `padding:0 0 10pt 8pt; width:32pt; text-align:right; font-weight:${fontWeight}; font-size:${fontSize}; vertical-align:bottom; white-space:nowrap;`;
            return `
              <tr>
                <td style="${labelCellStyle}">${escapeHtml(entry.label)}</td>
                <td style="${leaderCellStyle}"></td>
                <td style="${pageCellStyle}">${escapeHtml(String(entry.pageNumber))}</td>
              </tr>
            `;
          })
          .join("")}
      </table>
    </div>
  `.trim();
}

export function buildFindingPlainText(finding) {
  const document = buildFindingDocument(finding);

  return [
    document.title,
    `Severity: ${document.severity.label}`,
    "",
    ...document.sections.flatMap((section) => [section.title, section.value, ""]),
  ]
    .slice(0, -1)
    .join("\n");
}

export function buildSectionHtml(title, body) {
  const baseStyle = "font-family:'Poppins', Arial, sans-serif; font-size:12pt; line-height:1.5; color:#202124;";
  const h2Style = "font-family:'Poppins', Arial, sans-serif; font-size:18pt; line-height:1.4; margin:0 0 8pt; font-weight:700; color:#202124;";
  return `
    <div style="${baseStyle}">
      <h2 style="${h2Style}">${escapeHtml(title)}</h2>
      ${renderRichText(body)}
    </div>
  `.trim();
}

function buildGoogleDocsPageBreakHtml() {
  return `
    <p style="margin: 12pt 0 0; padding: 0; line-height: 1; font-size: 1pt; color: transparent; page-break-before: always; break-before: page;">&nbsp;</p>
  `.trim();
}

function buildWordPageBreakHtml() {
  return `
    <div style="page-break-before: always; break-before: page; mso-special-character: line-break; height: 1px; color: transparent;">&nbsp;</div>
  `.trim();
}

function buildFullReportBodyHtml(report, pageBreakHtml) {
  const sections = [];

  sections.push(buildCoverPageHtml(report));
  sections.push(buildTableOfContentsHtml(report));
  sections.push(buildConfidentialityNoticeHtml(report));
  sections.push(buildProjectDetailsHtml(report));

  if (report.executive_summary) {
    sections.push(buildSectionHtml("Executive Summary", report.executive_summary));
  }

  if (report.methodology_text) {
    sections.push(buildSectionHtml("Methodology", report.methodology_text));
  }

  if (report.findings?.length) {
    sections.push(...report.findings.map((finding) => buildFindingHtml(finding)));
  }

  return sections.map((section, index) => `${index > 0 ? pageBreakHtml : ""}${section}`).join("\n\n");
}

export function buildFullReportHtml(report) {
  return buildFullReportBodyHtml(report, buildGoogleDocsPageBreakHtml());
}

export function buildFullReportPlainText(report) {
  const sections = [];
  const toc = buildReportTableOfContentsDocument(report);
  const notice = buildConfidentialityNoticeDocument(report);
  const project = buildProjectDetailsDocument(report);
  const cover = buildCoverPageDocument(report);

  sections.push(
    [
      "Cover Page",
      "",
      `Date: ${cover.dateDisplay}`,
      `Title: ${cover.title}`,
      `Assessment Type: ${cover.subtitle}`,
      `Prepared By: ${cover.brandName}`,
      `Prepared For: ${cover.clientName}`,
    ].join("\n"),
  );

  sections.push(
    [
      toc.title,
      "",
      ...toc.entries.map((entry) => `${"  ".repeat(Math.max(0, entry.level - 1))}${entry.label} .... ${entry.pageNumber}`),
    ].join("\n"),
  );

  sections.push(
    [
      "Confidentiality Notice",
      "",
      notice.body,
    ].join("\n"),
  );

  sections.push(
    [
      "Project Details",
      "",
      project.overview,
      "",
      "Dates",
      `${project.dates[0].label}: ${project.dates[0].value}`,
      `${project.dates[1].label}: ${project.dates[1].value}`,
      `${project.dates[2].label}: ${project.dates[2].value}`,
      "",
      "Findings",
      ...project.findings.map((item) => `${item.label}: ${item.value}`),
      "",
      "Scope",
      project.scope || "[Add scope here]",
    ].join("\n"),
  );

  if (report.executive_summary) {
    sections.push(`Executive Summary\n\n${report.executive_summary}`);
  }

  if (report.methodology_text) {
    sections.push(`Methodology\n\n${report.methodology_text}`);
  }

  if (report.findings?.length) {
    sections.push(
      ...report.findings.map((finding) => buildFindingPlainText(finding)),
    );
  }

  return sections.join("\n\n");
}

export function buildFullReportHtmlDocument(report) {
  const reportTitle = escapeHtml(report.report_title || "Pentest Report");
  const body = buildFullReportBodyHtml(report, buildGoogleDocsPageBreakHtml());

  return `
<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <title>${reportTitle}</title>
    <style>
      @page {
        margin: 1in;
      }

      body {
        font-family: 'Poppins', Arial, sans-serif;
        font-size: 12pt;
        line-height: 1.5;
        color: #202124;
        max-width: 8.5in;
        margin: 0 auto;
      }

      .pb {
        page-break-before: always;
        break-before: page;
      }
    </style>
  </head>
  <body>
    ${body}
  </body>
</html>
  `.trim();
}

export function buildFullReportWordDocument(report) {
  const reportTitle = escapeHtml(report.report_title || "Pentest Report");
  const body = buildFullReportBodyHtml(report, buildWordPageBreakHtml());

  return `
<!doctype html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="utf-8">
    <title>${reportTitle}</title>
    <!--[if gte mso 9]>
    <xml>
      <w:WordDocument>
        <w:View>Print</w:View>
        <w:Zoom>100</w:Zoom>
      </w:WordDocument>
    </xml>
    <![endif]-->
    <style>
      @page {
        margin: 1in;
      }

      body {
        font-family: 'Poppins', Arial, sans-serif;
        font-size: 12pt;
        line-height: 1.5;
        color: #202124;
        max-width: 8.5in;
        margin: 0 auto;
      }
    </style>
  </head>
  <body>
    ${body}
  </body>
</html>
  `.trim();
}
